import * as Yup from 'yup';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
// routes

import useLocales from 'src/hooks/useLocales';

import { observer } from 'mobx-react-lite';
import { fData } from 'src/utils/formatNumber';
import { MuiPhone } from 'src/sections/@dashboard/MuiPhone';
import Fieldset from 'src/utils/fieldSet';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { ICustomer } from 'src/@types/Choqorak/MainTables/Contract/Customer';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { FormProvider, RHFSelect, RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import CustomFlag from 'src/sections/@dashboard/CustomFlags/CustomFlag';
import Iconify from 'src/components/Iconify';
import { useStore } from 'src/stores/store';
import CustomRHFAutocomplete from 'src/components/hook-form/CustomRHFAutocomplete';
import uuid from 'react-uuid';
import useThrottle from 'src/hooks/useThrottle';

// ----------------------------------------------------------------------

export default observer(function CustomerNewEditForm() {
  const language = window.localStorage.getItem('i18nextLng');
  const { CustomerStore, commonDropdown } = useStore();
  const [checked, setChecked] = useState(false);
  const { translate } = useLocales();
  const [isTrue, setIsTrue] = useState<boolean>(true);
  const [isImageUpdate, setIsImageUpdated] = useState(false);
  const [muiPhone, setMuiPhone] = useState('+93');
  const [muiEmergencyPhone, setMuiEmergencyPhone] = useState('+93');
  const cropperRef = useRef<ReactCropperElement>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [croppedImage, setCroppedImage] = useState<string>('');

  const { createCustomer, updateCustomer, editMode, clearSelectedCustomer, CustomerForEdit } =
    CustomerStore;
  const {
    BranchOption,
    loadProvinceDropdown,
    loadDistrictDropdown,
    ProvinceOption,
    DistrictOption,
    loadCurrentProvinceDropdown,
    loadCurrentDistrictDropdown,
    CurrentProvinceOption,
    CurrentDistrictOption,
  } = commonDropdown;
  const navigate = useNavigate();
  const [mainProvinceName, setMainProvinceName] = useState<string | undefined>('');
  const [currentProvinceName, setCurrentProvinceName] = useState<string | undefined>('');
  const [mainDistrictName, setMainDistrictName] = useState<string | undefined>('');
  const [currentDistrictName, setCurrentDistrictName] = useState<string | undefined>('');
  const { enqueueSnackbar } = useSnackbar();

  const NewCustomerSchema = Yup.object().shape({
    englishFirstName: Yup.string().required(`${translate('Validation.EnglishName')}`),
    pashtoFirstName: Yup.string().required(`${translate('Validation.PashtoName')}`),
    englishSurName: Yup.string().required(`${translate('Validation.EnglishSurName')}`),
    pashtoSurName: Yup.string().required(`${translate('Validation.PashtoSurName')}`),
    englishFatherName: Yup.string().required(
      `${translate('Validation.FatherEnglishNameIsRequired')}`
    ),
    mainProvinceName: Yup.string().required(`${translate('Validation.MainProvinceName')}`),
    mainDistrictName: Yup.string().required(`${translate('Validation.MainDistrictName')}`),
    currentProvinceName: Yup.string().required(`${translate('Validation.CurrentProvinceName')}`),
    currentDistrictName: Yup.string().required(`${translate('Validation.CurrentDistrictName')}`),
    pashtoFatherName: Yup.string().required(`${translate('Validation.FatherDariNameIsRequired')}`),
    englishGrandFatherName: Yup.string().required(`${translate('Validation.GrandFatherEnglish')}`),
    pashtoGrandFatherName: Yup.string().required(`${translate('Validation.GrandFatherPashto')}`),
    tazkiraNo: Yup.string().required(`${translate('Validation.CNIC')}`),
    dateOfBirth: Yup.date().required(`${translate('Validation.DateOfBirth')}`),
    temporaryAddress: Yup.string().required(`${translate('Validation.TemporaryAddress')}`),
    permenantAddress: Yup.string().required(`${translate('Validation.PermanentAddress')}`),
    // personalEmail: Yup.string().required(`${translate('Validation.personalEmail')}`),
    gender: Yup.string().required(`${translate('Validation.Gender')}`),
  });

  const defaultValues = useMemo<ICustomer>(
    () => ({
      customerId: CustomerForEdit?.customerId,
      englishFirstName: CustomerForEdit?.englishFirstName || '',
      pashtoFirstName: CustomerForEdit?.pashtoFirstName || '',
      englishSurName: CustomerForEdit?.englishSurName || '',
      pashtoSurName: CustomerForEdit?.pashtoSurName || '',
      englishFatherName: CustomerForEdit?.englishFatherName || '',
      pashtoFatherName: CustomerForEdit?.pashtoFatherName || '',
      englishGrandFatherName: CustomerForEdit?.englishGrandFatherName || '',
      pashtoGrandFatherName: CustomerForEdit?.pashtoGrandFatherName || '',
      gender: CustomerForEdit?.gender || '',
      tazkiraTypeId: CustomerForEdit?.tazkiraTypeId || 1,
      tazkiraNo: CustomerForEdit?.tazkiraNo || '',
      joldNo: CustomerForEdit?.joldNo || '',
      pageNo: CustomerForEdit?.pageNo || '',
      regNo: CustomerForEdit?.regNo || '',
      dateOfBirth: CustomerForEdit?.dateOfBirth || new Date(),
      temporaryAddress: CustomerForEdit?.temporaryAddress || '',
      permenantAddress: CustomerForEdit?.permenantAddress || '',
      BranchId: CustomerForEdit?.joinDate || undefined,
      mainProvinceId: CustomerForEdit?.mainProvinceId || undefined,
      mainDistrictId: CustomerForEdit?.mainDistrictId || undefined,
      currentProvinceId: CustomerForEdit?.currentProvinceId || undefined,
      currentDistrictId: CustomerForEdit?.currentDistrictId || undefined,
      bloodGroup: CustomerForEdit?.bloodGroup || '',

      personalEmail: CustomerForEdit?.personalEmail || '',
      phoneNumber: CustomerForEdit?.phoneNumber || '',
      emergencyPhoneNumber: CustomerForEdit?.emergencyPhoneNumber || '',
      isActive: CustomerForEdit?.isActive || true,
      photoPath: CustomerForEdit?.photoPath || '',
      profilePhoto: CustomerForEdit?.profilePhoto || null,
    }),
    [CustomerForEdit]
  );

  const methods = useForm<ICustomer>({
    resolver: yupResolver(NewCustomerSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = methods;
  const val = watch();
  const onSubmit = useThrottle((data: ICustomer) => {
    data.phoneNumber = muiPhone.replace(/\s+/g, '');
    data.emergencyPhoneNumber = muiEmergencyPhone.replace(/\s+/g, '');
    data.isActive = val.isActive;
    data.branchId = 1;

    if (data.customerId === undefined) {
      ///create
      data.profilePhoto = methods.getValues().profilePhoto;
      createCustomer(data)
        .then(() => {
          clearSelectedCustomer();
          enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
          navigate(PATH_DASHBOARD.Customer.list);
        })
        .catch((err: any) => {
          var json = JSON.parse(err.request.response);
          if (json.error.PersonalEmail != null) {
            setError('afterSubmit', { ...err, message: json.error.PersonalEmail });
          } else if (json.error.OfficialEmail != null) {
            setError('afterSubmit', { ...err, message: json.error.OfficialEmail });
          } else if (json.error.PhoneNumber != null) {
            setError('afterSubmit', { ...err, message: json.error.PhoneNumber });
          } else if (json.error.EmergencyPhoneNumber != null) {
            setError('afterSubmit', { ...err, message: json.error.EmergencyPhoneNumber });
          } else if (json.error.LeaveDate != null) {
            setError('afterSubmit', { ...err, message: json.error.LeaveDate });
          } else if (json.error.TazkiraTypeId != null) {
            setError('afterSubmit', { ...err, message: json.error.TazkiraTypeId });
          } else {
            setError('afterSubmit', { ...err, message: json.error });
          }
        });
    } else {
      ///update

      if (!isImageUpdate) {
        data.profilePhoto = null;
      } else {
        data.profilePhoto = methods.getValues().profilePhoto;
      }
      updateCustomer(data)
        .then(() => {
          clearSelectedCustomer();
          enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
          navigate(PATH_DASHBOARD.Customer.list);
        })
        .catch((err: any) => {
          var json = JSON.parse(err.request.response);
          if (json.error.PersonalEmail != null) {
            setError('afterSubmit', { ...err, message: json.error.PersonalEmail });
          } else if (json.error.OfficialEmail != null) {
            setError('afterSubmit', { ...err, message: json.error.OfficialEmail });
          } else if (json.error.PhoneNumber != null) {
            setError('afterSubmit', { ...err, message: json.error.PhoneNumber });
          } else if (json.error.EmergencyPhoneNumber != null) {
            setError('afterSubmit', { ...err, message: json.error.EmergencyPhoneNumber });
          } else if (json.error.LeaveDate != null) {
            setError('afterSubmit', { ...err, message: json.error.LeaveDate });
          } else if (json.error.TazkiraTypeId != null) {
            setError('afterSubmit', { ...err, message: json.error.TazkiraTypeId });
          } else {
            setError('afterSubmit', { ...err, message: json.error });
          }
        });
    }
  });

  // useEffect(() => {
  //   let value = String(val.isActive);
  //   // eslint-disable-next-line eqeqeq
  //   if (value != '0') {
  //     setIsTrue(!isTrue);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [val.isActive]);

  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
      setMuiPhone(defaultValues.phoneNumber!);
      setMuiEmergencyPhone(defaultValues.emergencyPhoneNumber!);
      setValue('gender', defaultValues.gender);
    }
    if (!editMode) {
      reset(defaultValues);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, editMode, defaultValues]);

  useEffect(() => {
    loadProvinceDropdown();
    loadCurrentProvinceDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (val.mainProvinceId) {
      loadDistrictDropdown(val.mainProvinceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val.mainProvinceId]);

  useEffect(() => {
    if (val.currentProvinceId) {
      loadCurrentDistrictDropdown(val.currentProvinceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val.currentProvinceId]);

  useEffect(() => {
    if (editMode) {
      if (defaultValues.mainProvinceId) {
        loadProvinceDropdown().then((res) => {
          setMainProvinceName(
            ProvinceOption.find((e) => e.value === defaultValues.mainProvinceId)?.text
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainProvinceName]);

  useEffect(() => {
    if (editMode) {
      if (defaultValues.currentProvinceId) {
        loadCurrentProvinceDropdown().then((res) => {
          setCurrentProvinceName(
            CurrentProvinceOption.find((e) => e.value === defaultValues.currentProvinceId)?.text
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProvinceName]);

  useEffect(() => {
    if (editMode) {
      if (defaultValues.mainProvinceId) {
        loadDistrictDropdown(val.mainProvinceId).then((res) => {
          setMainDistrictName(
            DistrictOption.find((e) => e.value === defaultValues.mainDistrictId)?.text
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainDistrictName]);

  useEffect(() => {
    if (editMode) {
      if (defaultValues.currentProvinceId) {
        loadCurrentDistrictDropdown(val.currentProvinceId).then((res) => {
          setCurrentDistrictName(
            CurrentDistrictOption.find((e) => e.value === defaultValues.currentDistrictId)?.text
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDistrictName]);

  useEffect(() => {
    setValue('mainProvinceName', mainProvinceName);
    setValue('currentProvinceName', currentProvinceName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainProvinceName, currentProvinceName]);

  useEffect(() => {
    setValue('mainDistrictName', mainDistrictName);
    setValue('currentDistrictName', currentDistrictName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainDistrictName, currentDistrictName]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setImageSrc(URL.createObjectURL(file));
      setCroppedImage('');
    }
  }, []);

  const handleCropImage = useCallback(() => {
    if (cropperRef.current) {
      if (editMode) {
        setIsImageUpdated(true);
      }
      const canvas = cropperRef.current?.cropper;
      if (canvas) {
        // Convert the canvas to a Blob object
        canvas.getCroppedCanvas().toBlob((blob: any) => {
          if (blob) {
            // Create a new File object with the Blob and set it as the value for 'profilePhoto'
            const croppedFile = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });

            setValue(
              'profilePhoto',
              Object.assign(croppedFile, {
                preview: URL.createObjectURL(croppedFile),
              })
            );

            setImageSrc('');
          }
        }, 'image/jpeg');
        // Convert the canvas to a data URL and set it as the cropped image
        const croppedDataURL = canvas.getCroppedCanvas().toDataURL();
        setCroppedImage(croppedDataURL);
      }
    }
  }, [editMode, setValue]);

  const gender = [
    { title: 'Male', value: 'Male' },
    { title: 'Female', value: 'Female' },
  ];

  const TazkiraType = [
    { title: 1, value: `${translate('Customer.ETazkira')}` },
    { title: 2, value: `${translate('Customer.PaperBaseTazkira')}` },
  ];

  const bloodGroup = [
    { title: 'A+', value: 'A+' },
    { title: 'A-', value: 'A-' },
    { title: 'B+', value: 'B+' },
    { title: 'B-', value: 'B-' },
    { title: 'O+', value: 'O+' },
    { title: 'O-', value: 'O-' },
    { title: 'AB+', value: 'AB+' },
    { title: 'AB-', value: 'AB-' },
  ];

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ py: 13, px: 3, borderRadius: '15px 15px 15px 15px' }}>
            <Fieldset legend={translate('Customer.ProfilePhoto')}>
              <Box sx={{ mb: 3 }}>
                <RHFUploadAvatar
                  name="profilePhoto"
                  accept="image/*"
                  onDrop={handleDrop}
                  maxSize={3145728}
                  showAsterisk={true}
                  helperText={
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 2,
                        mx: 'auto',
                        display: 'block',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      {translate('User.Allowed')}
                      <br /> {translate('User.MaxSize')} {fData(3145728)}
                    </Typography>
                  }
                />
                {imageSrc && (
                  <Box>
                    <Cropper
                      src={imageSrc}
                      style={{ width: '100%', height: '100%' }}
                      aspectRatio={16 / 16}
                      guides={true}
                      ref={cropperRef}
                    />
                    {croppedImage ? (
                      <Box>
                        <img src={croppedImage} alt="Cropped" />
                        <button onClick={() => handleCropImage()}>Cancel Crop</button>
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                        onClick={() => handleCropImage()}
                      >
                        {translate('Customer.CropImage')}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Fieldset>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, borderRadius: '15px 15px 15px 15px' }}>
            <Fieldset legend={translate('Customer.EnglishBioData')}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  // display: 'flex',
                }}
              >
                <RHFTextField
                  name="englishFirstName"
                  label={translate('Customer.EnglishFirstName')}
                  showAsterisk={true}
                  autoFocus
                />
                <RHFTextField
                  name="englishSurName"
                  label={translate('Customer.EnglishSurName')}
                  showAsterisk={true}
                />
                <RHFTextField
                  name="englishFatherName"
                  label={translate('Customer.EnglishFatherName')}
                  showAsterisk={true}
                />
                <RHFTextField
                  name="englishGrandFatherName"
                  label={translate('Customer.EnglishGrandFatherName')}
                  showAsterisk={true}
                />
              </Box>
            </Fieldset>

            <Fieldset legend={translate('Customer.PashtoDariBioData')}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  // display: 'flex',
                }}
              >
                <RHFTextField
                  name="pashtoFirstName"
                  label={translate('Customer.PashtoDariName')}
                  showAsterisk={true}
                />

                <RHFTextField
                  name="pashtoSurName"
                  label={translate('Customer.PashtoDariSurName')}
                  showAsterisk={true}
                />

                <RHFTextField
                  name="pashtoFatherName"
                  label={translate('Customer.PashtoDariFatherName')}
                  showAsterisk={true}
                />

                <RHFTextField
                  name="pashtoGrandFatherName"
                  label={translate('Customer.PashtoDariGrandFatherName')}
                  showAsterisk={true}
                />
              </Box>
            </Fieldset>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sx={{ mt: 1 }}>
          <Card sx={{ p: 3, borderRadius: '15px 15px 15px 15px' }}>
            <Fieldset legend={translate('Customer.ResidentialAddressInfo')}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
                }}
              >
                <CustomRHFAutocomplete
                  name="mainProvinceName"
                  label={translate('Customer.MainProvinceName')}
                  showAsterisk={true}
                  //placeholder={translate('Employee.Department')}
                  value={watch('mainProvinceName') || ''}
                  options={ProvinceOption.map((i) => i.text)}
                  getOptionLabel={(option: any) => `${option}`}
                  onChange={(event, newValue: any) => {
                    const find = ProvinceOption.filter((item) => item.text === newValue)[0];

                    if (find) {
                      const id = find?.value;
                      setValue('mainProvinceId', Number(id));
                      setValue(`mainProvinceName`, find?.text);
                    } else {
                      setValue('mainProvinceId', undefined);
                      setValue(`mainProvinceName`, '');
                    }
                  }}
                  freeSolo
                  fullWidth
                  renderOption={(props, option: any) => (
                    <li {...props} key={option + '-' + uuid()}>
                      {option}
                    </li>
                  )}
                />

                <CustomRHFAutocomplete
                  name="mainDistrictName"
                  label={translate('Customer.MainDistrictName')}
                  showAsterisk={true}
                  //placeholder={translate('Employee.Department')}
                  value={watch('mainDistrictName') || ''}
                  options={DistrictOption.map((i) => i.text)}
                  getOptionLabel={(option: any) => `${option}`}
                  onChange={(event, newValue: any) => {
                    const find = DistrictOption.filter((item) => item.text === newValue)[0];

                    if (find) {
                      const id = find?.value;
                      setValue('mainDistrictId', Number(id));
                      setValue(`mainDistrictName`, find?.text);
                    } else {
                      setValue('mainDistrictId', undefined);
                      setValue(`mainDistrictName`, '');
                    }
                  }}
                  freeSolo
                  fullWidth
                  renderOption={(props, option: any) => (
                    <li {...props} key={option + '-' + uuid()}>
                      {option}
                    </li>
                  )}
                />

                <RHFTextField
                  name="permenantAddress"
                  label={translate('Customer.PermanentAddress')}
                  showAsterisk={true}
                  multiline
                  maxRows={4}
                  minRows={4}
                />

                <CustomRHFAutocomplete
                  name="currentProvinceName"
                  label={translate('Customer.CurrentProvinceName')}
                  showAsterisk={true}
                  //placeholder={translate('Employee.Department')}
                  value={watch('currentProvinceName') || ''}
                  options={CurrentProvinceOption.map((i) => i.text)}
                  getOptionLabel={(option: any) => `${option}`}
                  onChange={(event, newValue: any) => {
                    const find = CurrentProvinceOption.filter((item) => item.text === newValue)[0];

                    if (find) {
                      const id = find?.value;
                      setValue('currentProvinceId', Number(id));
                      setValue(`currentProvinceName`, find?.text);
                    } else {
                      setValue('currentProvinceId', undefined);
                      setValue(`currentProvinceName`, '');
                    }
                  }}
                  freeSolo
                  fullWidth
                  renderOption={(props, option: any) => (
                    <li {...props} key={option + '-' + uuid()}>
                      {option}
                    </li>
                  )}
                />

                <CustomRHFAutocomplete
                  name="currentDistrictName"
                  label={translate('Customer.CurrentDistrictName')}
                  showAsterisk={true}
                  //placeholder={translate('Employee.Department')}
                  value={watch('currentDistrictName') || ''}
                  options={CurrentDistrictOption.map((i) => i.text)}
                  getOptionLabel={(option: any) => `${option}`}
                  onChange={(event, newValue: any) => {
                    const find = CurrentDistrictOption.filter((item) => item.text === newValue)[0];

                    if (find) {
                      const id = find?.value;
                      setValue('currentDistrictId', Number(id));
                      setValue(`currentDistrictName`, find?.text);
                    } else {
                      setValue('currentDistrictId', undefined);
                      setValue(`currentDistrictName`, '');
                    }
                  }}
                  freeSolo
                  fullWidth
                  renderOption={(props, option: any) => (
                    <li {...props} key={option + '-' + uuid()}>
                      {option}
                    </li>
                  )}
                />

                <RHFTextField
                  name="temporaryAddress"
                  label={translate('Customer.TemporaryAddress')}
                  showAsterisk={true}
                  multiline
                  maxRows={4}
                  minRows={4}
                />
              </Box>
            </Fieldset>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} sx={{ mt: 1 }}>
          <Card sx={{ p: 3, borderRadius: '15px 15px 15px 15px' }}>
            <Fieldset legend={translate('Customer.TazkiraInfo')}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' },
                }}
              >
                <RHFSelect name="gender" label={translate('Customer.Gender')}>
                  <option value="" />
                  {gender.map((op) => (
                    <option key={op.title} value={op.title}>
                      {op.title}
                    </option>
                  ))}
                </RHFSelect>
                <RHFSelect
                  name="tazkiraTypeId"
                  label={translate('Customer.TazkiraType')}
                  onChange={(e) => setValue('tazkiraTypeId', parseInt(e.target.value))}
                >
                  <option value="" />
                  {TazkiraType.map((op) => (
                    <option key={op.title} value={op.title}>
                      {op.value}
                    </option>
                  ))}
                </RHFSelect>

                <RHFTextField
                  name="tazkiraNo"
                  label={translate('Customer.CNIC')}
                  showAsterisk={true}
                />

                {val.tazkiraTypeId === 2 && (
                  <>
                    <RHFTextField name="joldNo" label={translate('Customer.JoldNo')} />
                    <RHFTextField name="pageNo" label={translate('Customer.PageNo')} />
                    <RHFTextField name="regNo" label={translate('Customer.RegNo')} />
                  </>
                )}

                <LocalizDatePicker
                  name="dateOfBirth"
                  label={translate('Customer.DateOfBirth')}
                  control={control}
                  showAsterisk={true}
                />
              </Box>
            </Fieldset>

            <Fieldset legend={translate('Customer.CustomerGeneralInfo')}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' },
                }}
              >
                <RHFSelect name="bloodGroup" label={translate('Customer.BloodGroup')}>
                  <option value="" />
                  {bloodGroup.map((op) => (
                    <option key={op.value} value={op.title}>
                      {op.title}
                    </option>
                  ))}
                </RHFSelect>

                <RHFTextField name="personalEmail" label={translate('Customer.PersonalEmail')} />
                <MuiPhone
                  value={muiPhone}
                  dir={language === 'en' ? 'ltr' : 'ltr'}
                  onChange={(Phone) => setMuiPhone(Phone)}
                  name="phoneNumber"
                  customFlag={CustomFlag}
                />
                <MuiPhone
                  value={muiEmergencyPhone}
                  dir={language === 'en' ? 'ltr' : 'ltr'}
                  label={translate('Customer.EmergencyPhoneNumber')}
                  onChange={(EmergencyPhone) => setMuiEmergencyPhone(EmergencyPhone)}
                  name="emergencyPhoneNumber"
                  customFlag={CustomFlag}
                />

                {editMode ? (
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            onChange={(e) => {
                              field.onChange(e); // Notify React Hook Form of the value change
                              handleChange(e); // Handle the switch state change
                            }}
                            checked={field.value}
                          />
                        }
                        label={translate('Customer.isActive')}
                        labelPlacement="end"
                      />
                    )}
                  />
                ) : (
                  <></>
                )}
              </Box>
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  startIcon={
                    !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                  }
                >
                  {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
                </LoadingButton>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                  onClick={() => {
                    clearSelectedCustomer();
                    navigate(PATH_DASHBOARD.Customer.list);
                  }}
                >
                  {translate('CRUD.BackToList')}
                </Button>
              </Stack>
            </Fieldset>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
